import { ApolloError } from '@apollo/client';
import React, { useCallback, useMemo, useState } from 'react';

import CartError, { SelectNewRestaurantActionButton } from 'components/cart-error';
import { useCartContext } from 'state/cart';
import { OrderStatus } from 'state/order';
import { useRestaurantPosConnectivityStatus } from 'state/restaurant-connectivity-status';
import { useStoreContext } from 'state/store';
import { GraphQLErrorCodes, graphQLErrorsIncludeExtensionCode } from 'utils/errors';

import { useDeliveryErrorModalCopy } from './hooks/use-delivery-error-modal-copy';
import { IErrorsListItems } from './types';

export const CartPageError = () => {
  const { isStoreOpenAndAvailable, store } = useStoreContext();
  const {
    serverOrder,
    unavailableCartEntries,
    priceOrderFailure,
    priceMutationError,
    dismissUnavailableErrorModal,
    calculatedCartTotal,
    orderStatus,
  } = useCartContext();
  const isCartTotalNegative = calculatedCartTotal < 0;
  const storeId = store.number || '';
  const { isRestaurantPosOnline } = useRestaurantPosConnectivityStatus({
    storeId,
  });

  const [isOpen, setIsOpen] = useState(true);

  const handleDismiss = useCallback(() => {
    setIsOpen(false);
    dismissUnavailableErrorModal();
  }, [dismissUnavailableErrorModal]);

  const restaurantUnavailableError = graphQLErrorsIncludeExtensionCode(
    priceMutationError instanceof ApolloError ? priceMutationError.graphQLErrors : [],
    GraphQLErrorCodes.RESTAURANT_UNAVAILABLE
  );
  const unavailableCartItems = unavailableCartEntries.length > 0;
  const errorsList: IErrorsListItems = [
    {
      priceMutationError: !restaurantUnavailableError && Boolean(priceMutationError),
    },
    { priceOrderFailure },
    { unavailableCartItems },
    { isRestaurantPosOnline: !isRestaurantPosOnline },
    { isStoreOpenAndAvailable: !isStoreOpenAndAvailable || restaurantUnavailableError },
  ];
  const { errorModalBody, errorModalTitle } = useDeliveryErrorModalCopy(serverOrder, errorsList);

  const actionButtons = useMemo(() => {
    return <SelectNewRestaurantActionButton onDismiss={handleDismiss} />;
  }, [handleDismiss]);

  const hasOrderFailures =
    orderStatus &&
    [
      OrderStatus.INSERT_ERROR,
      OrderStatus.PAYMENT_ERROR,
      OrderStatus.VALIDATION_ERROR,
      OrderStatus.CATERING_ERROR,
    ].includes(orderStatus);

  if (hasOrderFailures) {
    return null;
  }

  return (
    <CartError
      isOpen={isOpen}
      body={errorModalBody}
      title={errorModalTitle}
      actionButtons={actionButtons}
      onDismiss={handleDismiss}
      additionalLoggingContext={{
        priceMutationError,
        priceOrderFailure,
        isRestaurantPosOnline,
        unavailableCartEntriesLength: unavailableCartEntries.length,
        isCartTotalNegative,
      }}
    />
  );
};
